import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, MessageCircle } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { rp, fmtDateTime, ORDER_STATUS, waLink } from "@/lib/format";
import { useSettings, useSeo } from "@/lib/shop";
import { OrderStatusBadge, PaymentBadge } from "@/components/site/Badges";

const FLOW = ["menunggu_konfirmasi", "menunggu_pembayaran", "menunggu_verifikasi", "diproses", "siap_diambil", "dalam_pengiriman", "selesai"];
const inp = "w-full h-12 rounded-xl border-2 border-neutral-200 px-4 focus:border-neutral-900 outline-none";

function Steps({ o }) {
  const steps = FLOW.filter((s) => (o.fulfillment === "pickup" ? s !== "dalam_pengiriman" : s !== "siap_diambil"));
  const cur = steps.indexOf(o.status);
  if (o.status === "dibatalkan") return <p className="rounded-lg bg-red-50 border border-red-200 p-3 text-red-800 text-sm font-semibold" data-testid="track-cancelled">Pesanan ini telah dibatalkan.</p>;
  return (
    <ol className="grid grid-cols-2 gap-2 sm:grid-cols-6" data-testid="track-steps">
      {steps.map((s, i) => (
        <li key={s} className={`rounded-lg px-3 py-2 text-xs font-semibold border ${i <= cur ? "bg-[var(--bk-yellow)] border-neutral-900 text-neutral-900" : "bg-white text-neutral-400"}`}>{ORDER_STATUS[s].label}</li>
      ))}
    </ol>
  );
}

function Result({ o }) {
  const s = useSettings();
  return (
    <div className="bg-white rounded-2xl border-2 border-neutral-900 p-5 sm:p-6 space-y-5" data-testid="track-result">
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div>
          <div className="text-xs text-neutral-500">{o.kind === "po" ? "Pesanan Pre-Order" : "Pesanan Ready Stock"} · {fmtDateTime(o.created_at)}</div>
          <div className="font-mono text-xl font-bold" data-testid="track-order-no">{o.order_no}</div>
          <div className="text-sm">Atas nama {o.customer_name} · {o.fulfillment === "pickup" ? "Ambil di toko" : "Pengiriman"}</div>
        </div>
        <div className="flex gap-2"><span data-testid="track-status"><OrderStatusBadge status={o.status} /></span><span data-testid="track-payment"><PaymentBadge status={o.payment_status} /></span></div>
      </div>
      <Steps o={o} />
      <div className="divide-y border rounded-xl">
        {o.items.map((it, i) => (
          <div key={i} className="p-3 text-sm">
            <div className="font-semibold">{it.name}{it.variant && ` (${it.variant})`}</div>
            <div className="flex justify-between"><span>{it.qty} × {rp(it.unit_price)}</span><b className="price">{rp(it.subtotal)}</b></div>
            {o.kind === "po" && <div className="text-xs text-neutral-600">DP {rp(it.dp_total)} · sisa {rp(it.remaining_total)}{it.eta && ` · Estimasi tiba ${it.eta}`}</div>}
          </div>
        ))}
      </div>
      <div className="text-sm space-y-1 text-right">
        <div>Total: <b className="price">{rp(o.total)}</b></div>
        {o.kind === "po" && <div>DP: <b>{rp(o.dp_total)}</b> · Sisa pelunasan: <b>{rp(o.remaining_total)}</b></div>}
        <div>Ongkir: {o.shipping_fee === null ? (o.fulfillment === "pickup" ? "-" : "dikonfirmasi admin") : rp(o.shipping_fee)}</div>
        <div>Pembayaran terverifikasi: <b data-testid="track-paid">{rp(o.paid_amount)}</b></div>
      </div>
      <div>
        <h3 className="font-heading font-bold mb-2">Riwayat status</h3>
        <ol className="border-l-2 border-neutral-200 pl-4 space-y-1 text-sm">
          {o.history.map((h, i) => <li key={i}><b>{ORDER_STATUS[h.status]?.label}</b> <span className="text-xs text-neutral-500">· {fmtDateTime(h.at)}</span></li>)}
        </ol>
      </div>
      <a href={waLink(s.whatsapp, `Halo Bengkel Toys & Hobbies, saya ingin menanyakan pesanan ${o.order_no}.`)} target="_blank" rel="noreferrer" className="btn-wa" data-testid="track-ask-wa"><MessageCircle className="h-4 w-4" />Tanya admin via WhatsApp</a>
      {o.nota_token && <a href={`/nota/${o.order_no}?t=${o.nota_token}`} className="btn-brand ml-2" data-testid="track-nota-link">Lihat Nota</a>}
    </div>
  );
}

export default function TrackOrder() {
  useSeo("Lacak Pesanan", "Cek status pesanan Bengkel Toys & Hobbies dengan nomor pesanan dan nomor WhatsApp.");
  const [sp] = useSearchParams();
  const [f, setF] = useState({ order_no: sp.get("no") || "", phone: "" });
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setRes(null);
    setBusy(true);
    try {
      setRes((await api.post("/orders/track", f)).data);
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="container-x py-10 sm:py-14 max-w-3xl" data-testid="track-page">
      <h1 className="font-heading text-3xl sm:text-4xl font-extrabold">Lacak Pesanan</h1>
      <p className="text-neutral-600 mt-2">Masukkan nomor pesanan (mis. BTR-261020-0001) dan nomor WhatsApp yang dipakai saat memesan.</p>
      <form onSubmit={submit} className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 mt-6">
        <input data-testid="track-order-input" required className={`${inp} font-mono uppercase`} placeholder="Nomor pesanan" value={f.order_no} onChange={(e) => setF({ ...f, order_no: e.target.value })} />
        <input data-testid="track-phone-input" required inputMode="tel" className={inp} placeholder="No. WhatsApp, mis. 0812..." value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
        <button data-testid="track-submit" disabled={busy} className="btn-ink h-12"><Search className="h-4 w-4" />{busy ? "Mencari..." : "Lacak"}</button>
      </form>
      {err && <p className="mt-4 rounded-lg bg-red-50 border border-red-200 p-3 text-red-800 text-sm" data-testid="track-error">{err}</p>}
      {res && <div className="mt-8"><Result o={res} /></div>}
    </div>
  );
}
